import { BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
/**
* @class LinkedAccount
* @classdesc Class definition for record type from model
* @param {OBJECT} record - record from model to init class
* @extends BCMSRecord
* @example const record = model.linkedAccounts[0];
* const linkedAccount = new LinkedAccount(record);
* @property {NUMBER} _id - The id of the Account
* @property {STRING} _name - The name of the account
* @property {STRING} _nickname - The custom name for the account
* @property {STRING} _number - the last four of account
* @property {STRING} _numberSecondary - the last four of account with a hash
* @property {STRING} _type - represents the account type
* @property {NUMBER} _typeID - ID of sub type for additional information
* @property {BOOLEAN} _canTransferFrom - can the account be transferred from
* @property {bcmsDate} _dateOpened - days in past account was opened
* @property {BOOLEAN} _isMobilePrimary - is this the primary mobile account
* @property {bcmsDate} _lastDownloadDate - number of days in the past for last download
* @property {STRING} _balanceLabel - The label for the balance
* @property {STRING} _secondaryBalanceLabel - The label for the secondary balance
* @property {STRING} _statementPreference - The statement preference of the account
* @property {bcmsCurrency} _availableBalance - balance available in the account
* @property {STRING} _interestRate - place holder for showing the interest rate for the account
* @property {STRING} _ytdInterest - dollar amount of interest paid for year
* @property {bcmsCurrency} _currentBalance - The current balance of the account
*
* @description When a new "Account" is created in the model xml, 
* create a "mobileNickname" entry in the model xml as well.
* This is probably how the actual product works since the user 
* can't create a "Mobile Nickname" in the product,
* but they have access to mobile nickname entries they can edit.
* 
*/

export default class LinkedAccount extends BCMSRecord {
  _id
  _name
  _nickname
  _number
  _numberSecondary
  _type
  _canTransferFrom
  _dateOpened
  _isMobilePrimary
  _lastDownloadDate
  _balanceLabel
  _secondaryBalanceLabel
  _statementPreference
  _isExternal
  _institutionName
  _routingNumber
  _externalStatus
  _availableBalance
  _currentBalance
  _interestRate
  _ytdInterest

  constructor(record) {
    super(record)
  }
}

LinkedAccount.define({
  "recordType": "linkedAccounts",
  "recordIDProp": "id",
  "className" : "LinkedAccount",
  "recordProperties": [

   /** 
    * @name id
    * @instance
    * @property get {NUMBER} - gets the id of the account
    * @property set {NUMBER} - sets the id of the account
    * @memberof Account 
    */

    BCMSRecordProperty.mapToString("id", "_id"),

    /** 
     * @name name
     * @instance
     * @property get {STRING} - gets the name of the account
     * @property set {STRING} - sets the name of the account
     * @memberof Account 
     */

    BCMSRecordProperty.mapToString("name", "_name"),

    /** 
     * @name nickname
     * @instance
     * @property get {STRING} - gets the nickname of the account
     * @property set {STRING} - sets the nickname of the account
     * @memberof Account 
     */

    BCMSRecordProperty.mapToString("nickname", "_nickname"),

   /** 
    * @name number
    * @instance
    * @property get {STRING} - gets the number of the account
    * @property set {STRING} - sets the number of the account
    * @memberof Account 
    */

    BCMSRecordProperty.mapToString("number", "_number"),

    /** 
    * @name numberSecondary
    * @instance
    * @property get {STRING} - gets the numberSecondary of the account
    * @property set {STRING} - sets the numberSecondary of the account
    * @memberof Account 
    */

    BCMSRecordProperty.mapToString("numberSecondary", "_numberSecondary"),

   /** 
    * @name type
    * @instance
    * @property get {STRING} - gets the type of the account
    * @property set {STRING} - sets the type of the account
    * @memberof Account 
    */

    BCMSRecordProperty.mapToString("type", "_type"),

    /** 
     * @name canTransferFrom
     * @instance
     * @property get {STRING} - gets the typeID of the account
     * @property set {STRING} - sets the typeID of the account
     * @memberof Account 
     */

    BCMSRecordProperty.mapToString("canTransferFrom", "_canTransferFrom"),

   /** 
    * @name dateOpened
    * @instance
    * @property get {bcmsDate} - gets the dateOpened of the account
    * @property set {bcmsDate} - sets the dateOpened of the account
    * @memberof Account 
    */   

    BCMSRecordProperty.mapToDate("dateOpened", "_dateOpened"),

    /** 
     * @name balanceLabel
     * @instance
     * @property get {STRING} - gets the balanceLabel of the account
     * @property set {STRING} - sets the balanceLabel of the account
     * @memberof Account 
     */

    BCMSRecordProperty.mapToString("balanceLabel", "_balanceLabel"),

    /**
     * @name secondaryBalanceLabel
     * @instance
     * @property get {STRING} - gets the secondaryBalanceLabel of the account
     * @property set {STRING} - sets the secondaryBalanceLabel of the account
     * @memberof Account
     */
    BCMSRecordProperty.mapToString("secondaryBalanceLabel", "_secondaryBalanceLabel"),

  /** 
    * @name hasFastBalance
    * @instance
    * @property get {BOOLEAN} - gets the hasFastBalance of the account
    * @property set {BOOLEAN} - sets the hasFastBalance of the account
    * @memberof Account 
    */   

    BCMSRecordProperty.mapToBoolean("hasFastBalance", "_hasFastBalance"),

   /** 
    * @name isMobilePrimary
    * @instance
    * @property get {BOOLEAN} - gets the isMobilePrimary of the account
    * @property set {BOOLEAN} - sets the isMobilePrimary of the account
    * @memberof Account 
    */

    BCMSRecordProperty.mapToBoolean("isMobilePrimary", "_isMobilePrimary"),

   /** 
    * @name lastDownloadDate
    * @instance
    * @property get {bcmsDate} - gets the lastDownloadDate of the account
    * @property set {bcmsDate} - sets the lastDownloadDate of the account
    * @memberof Account 
    */

    BCMSRecordProperty.mapToDate("lastDownloadDate", "_lastDownloadDate"),

    /**
     * @name statementPreference
     * @instance
     * @property get {STRING} - gets the statementPreference of the account
     * @property set {STRING} - sets the statementPreference of the account
     * @memberof Account
     */
    BCMSRecordProperty.mapToString("statementPreference", "_statementPreference"),

    /**
     * @name isExternal
     * @instance
     * @property get {STRING} - gets the external value of the account
     * @property set {STRING} - sets the external value of the account
     * @memberof Account
     */
    BCMSRecordProperty.mapToBoolean("isExternal", "_isExternal"),

    /**
     * @name institutionName
     * @instance
     * @property get {STRING} - gets the institution name of the account
     * @property set {STRING} - sets the institution name of the account
     * @memberof Account
     */
    BCMSRecordProperty.mapToString("institutionName", "_institutionName"),

    /** 
    * @name routingNumber
    * @instance
    * @property get {NUMBER} - gets the routing number of the account
    * @property set {NUMBER} - sets the routing number of the account
    * @memberof Account 
    */
    BCMSRecordProperty.mapToNumber("routingNumber", "_routingNumber"),

    /**
     * @name externalStatus
     * @instance
     * @property get {STRING} - gets the external status of the account, like "active".
     * @property set {STRING} - sets the external status of the account, like "active".
     * @memberof Account
     */
    BCMSRecordProperty.mapToString("externalStatus", "_externalStatus"),

    /** 
     * @name availableBalance
     * @instance
     * @property get {bcmsCurrency} - gets the availableBalance of the checkingAccount
     * @property set {bcmsCurrency} - sets the availableBalance of the checkingAccount
     * @memberof CheckingAccount 
     */
    BCMSRecordProperty.mapToCurrency("availableBalance","_availableBalance"),
    /** 
     * @name interestRate
     * @instance
     * @property get {STRING} - gets the interestRate of the checkingAccount
     * @property set {STRING} - sets the interestRate of the checkingAccount
     * @memberof CheckingAccount 
     */
    BCMSRecordProperty.mapToString("interestRate","_interestRate"),
    /** 
     * @name ytdInterest
     * @instance
     * @property get {STRING} - gets the ytdInterest of the checkingAccount
     * @property set {STRING} - sets the ytdInterest of the checkingAccount
     * @memberof CheckingAccount 
     */
    BCMSRecordProperty.mapToString("ytdInterest","_ytdInterest"),

    /**
     * @name currentBalance
     * @instance
     * @property get {bcmsCurrency} - gets the currentBalance of the checkingAccount
     * @property set {bcmsCurrency} - sets the currentBalance of the checkingAccount
     * @memberof CheckingAccount
     */
    BCMSRecordProperty.mapToCurrency("currentBalance", "_currentBalance")
  ]
})




